import { imageSrcset, imageVariantWidth, originalImageUrl } from '../../utils/image-url';
import { observeImageOnce } from '../../utils/image-intersection';
import { ChangeDetectorRef, Directive, ElementRef, HostBinding, Input, PLATFORM_ID, OnInit, OnDestroy, inject, input } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { USER_AGENT } from '../../tokens/user-agent.token';
import { heroWidth, IMAGE_ORIGIN, imageWidthUrl, webpImageUrl } from '../../utils/image-url';

@Directive({
    host: { '(error)': 'imageError($event)' },
    selector: '[appLazyload]',
    standalone: false
})
export class LazyloadDirective implements OnInit, OnDestroy {
  private stopObserving: (() => void) | null = null;
  private destroyed = false;

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopObserving?.();
    this.stopObserving = null;
  }
  private el = inject(ElementRef);
  private platformId = inject(PLATFORM_ID);
  // Los HostBinding (src/srcset/class) solo se vuelcan al DOM cuando Angular revisa
  // la vista del componente anfitrión. Dentro de un componente OnPush (category-card,
  // tras la migración) el callback del IntersectionObserver cambiaba srcAttr pero
  // nadie marcaba la vista: los chips de categoría se quedaban con el placeholder
  // inline para siempre (antes lo "salvaba" el evento load de la miniatura /r/10).
  private cdr = inject(ChangeDetectorRef);
  // El MISMO User-Agent en servidor (lo provee server.ts desde la petición) y en
  // cliente (main.ts lo provee desde navigator.userAgent): así el ancho del hero se
  // decide igual en los dos lados y el preload del servidor se aprovecha entero.
  private userAgent = inject(USER_AGENT, { optional: true }) ?? '';


  @HostBinding('attr.srcset') srcsetAttr = null;
  @HostBinding('attr.src') srcAttr = null;
  // Ancho CSS conocido: el navegador elige la variante antes de hidratar.
  @HostBinding('attr.sizes') sizesAttr = null;

  @HostBinding('class') elementClass = '';

  // Sólo se emiten en modo priority (imagen LCP).
  @HostBinding('attr.fetchpriority') fetchPriorityAttr = null;
  @HostBinding('attr.decoding') decodingAttr = null;
  @HostBinding('attr.loading') loadingAttr = null;

  readonly src = input<string>(undefined);
  readonly srcset = input<string>(undefined);
  @Input() blurred = true;
  @Input() webp = true;
  @Input() resize = true;
  readonly createMode = input(false);
  /**
   * Imagen crítica (LCP): se carga la foto real de inmediato, sin placeholder de
   * 10px ni IntersectionObserver, con fetchpriority=high y a ancho fijo para casar
   * con el <link rel=preload> que inyecta el servidor (heroImageUrl en social-meta.ts).
   */
  readonly priority = input(false);

  /**
   * Imagen above-the-fold (primeras tarjetas del feed): se emite la URL REAL
   * directamente, sin placeholder /r/10 ni IntersectionObserver, para que el HTML
   * del SSR ya la traiga nítida y pinte sin esperar al arranque del bundle —
   * elimina el parpadeo borroso→nítido y desacopla la imagen del JS. A diferencia
   * de priority (hero 800px de la ficha), usa un ancho fijo pequeño acorde al hueco
   * de la tarjeta. El ancho es FIJO (no offsetWidth) para que servidor y cliente
   * emitan la MISMA URL y la hidratación no detecte mismatch.
   */
  readonly eager = input(false);
  readonly eagerWidth = input(166);
  /**
   * Segundo candidato del srcset en modo eager, para pantallas de mayor densidad
   * (IMG-8). Por defecto eagerWidth*1.5 (si eagerWidth ya está pensado a DPR2, esto
   * cubre DPR3: 166→249). Se calcula en formatEagerImage(), no aquí, porque un @Input
   * no puede leer de forma fiable OTRO @Input en su inicializador (Angular asigna los
   * inputs después del constructor). Pásalo explícito cuando la proporción por
   * defecto no encaje (p. ej. el logo de tienda, pensado a DPR1).
   */
  readonly eagerWidth2 = input<number>(null);
  /**
   * Atributo sizes del <img> eager: ancho CSS real de la caja. Por defecto
   * eagerWidth/2 (asume eagerWidth ya pensado a DPR2); pásalo explícito si la caja no
   * cuadra con esa proporción. Determinista servidor/cliente (no depende de offsetWidth).
   */
  readonly eagerSizes = input<string>(null);

  /** Opt-in for components with a known CSS image size. No DOM measurement needed. */
  readonly responsiveWidths = input<readonly number[]>(null);
  readonly responsiveSizes = input<string>(null);

  /**
   * Ancho fijo (/r/N/) con el que el SERVIDOR emite la URL real de las imágenes lazy
   * (las que no son eager ni priority). Desde el arranque diferido del bundle (import
   * en la 1ª interacción o a los 3 s) el HTML tiene que traer ya la foto: con el
   * placeholder inline las tarjetas se veían en gris hasta que arrancaba Angular.
   * El navegador las carga por su cuenta con loading="lazy" (0 JS). Por defecto
   * eagerWidth (166, miniatura de tarjeta a DPR2); pásalo menor en cajas pequeñas
   * (chips de categoría, avatares) para no descargar de más. Al hidratar, el cliente
   * ADOPTA esa URL (adoptSsrImage) y solo pide otra mayor si la caja lo justifica.
   */
  readonly ssrWidth = input<number>(null);

  /** Ancho /r/N/ de la URL que dejó el SSR y que el cliente adoptó (0 = sin /r/N/). */
  private adoptedWidth = 0;
  /** true cuando el cliente ha adoptado la foto real que ya traía el HTML del SSR. */
  private adopted = false;

  /**
   * Placeholder inline (0 peticiones) para imágenes aún no cargadas en modo blurred
   * (IMG-3). Sustituye al antiguo src real /r/10/... del SSR, que generaba una
   * petición HTTP desechable por cada imagen bajo el pliegue (y hacía que el avatar
   * se descargara dos veces: /r/10 y luego la buena). Hoy solo se usa en cliente
   * (navegación CSR o imágenes que el SSR no pudo emitir): el IntersectionObserver
   * pone después la URL real (loadImage()). Es un cuadrado 1x1 gris medio, sin
   * declarar width/height propios para que el navegador lo estire igual que hacía la
   * miniatura /r/10.
   */
  private static readonly PLACEHOLDER_SRC =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3Crect width='1' height='1' fill='%23f4f2f2'/%3E%3C/svg%3E";

  private static readonly FALLBACK_SRC = '/assets/images/logo_quad.webp';

  /**
   * Ancho en píxeles reales que necesita la caja (offsetWidth × DPR, con los topes
   * de siempre), o 0 si no procede redimensionar (sin resize, createMode, caja oculta).
   */
  private neededWidth(): number {
    if (!this.resize || this.createMode()) return 0;
    const offsetWidth = this.el.nativeElement.offsetWidth;
    if (!(offsetWidth > 0)) return 0;

    let width = parseInt(offsetWidth);

    // En mobile, reducir resolución para ahorrar datos
    // devicePixelRatio > 1 indica pantalla de alta densidad
    const isMobile = window && window.innerWidth < 768;
    const dpr = window && window.devicePixelRatio || 1;

    if (isMobile) {
      // En mobile, limitar el ancho máximo y ajustar por DPR
      width = Math.min(width * Math.min(dpr, 2), 800); // Máx 800px en mobile
    } else {
      width = width * Math.min(dpr, 2); // Desktop limitar a 2x
    }

    return imageVariantWidth(Math.ceil(width));
  }

  formatImage(image: string) {
    if (!image || this.createMode()) return image;

    const width = this.neededWidth();
    if (width) image = this.setResolutionInUrl(image, width);

    // Anclado a la extensión final: sin anclar, un "jpg" o "png" en cualquier parte
    // de la ruta (nombre del chollo, slug de tienda) corrompía la URL, y ".jpeg"
    // acababa convertido en ".webpeg" porque "jpg" alternaba antes que "jpeg".
    if (this.webp) image = webpImageUrl(image);

    return image;
  }

  ngOnInit() {
    const src = this.src();
    if (src && !IMAGE_ORIGIN.test(src)) {
      this.blurred = false;
      this.webp = false;
      this.resize = false;
    }

    if (this.createMode()) {
      this.srcAttr = src;
      return;
    }

    if (this.priority()) {
      // Imagen LCP: sin placeholder ni IntersectionObserver. Se pinta ya la foto
      // real, a ancho fijo (mismo que el preload del servidor) y con prioridad alta.
      //
      // Si el nodo viene del SSR se adopta su URL TAL CUAL en vez de recalcularla: el
      // <link rel=preload> del servidor ya la está bajando y el ancho lo decidió él,
      // así que cualquier desacuerdo aquí (un User-Agent que el cliente vea distinto)
      // costaría una segunda descarga de la foto entera en vez de un píxel de nitidez.
      if (!this.adoptSsrImage()) {
        const url = this.formatPriorityImage(src);
        this.srcAttr = url;
        const srcset = this.srcset();
        this.srcsetAttr = srcset ? this.formatPriorityImage(srcset) : url;
      }
      this.fetchPriorityAttr = 'high';
      this.decodingAttr = 'async';
      this.loadingAttr = 'eager';
      return;
    }

    if (this.loadResponsiveImage()) return;

    if (this.eager()) {
      // Tarjeta above-the-fold: foto real de inmediato (también en SSR), sin blur.
      const url = this.formatEagerImage(src, this.eagerWidth());
      this.srcAttr = url;

      // srcset real con 2 candidatos + sizes al ancho CSS de la caja (IMG-8), para
      // que DPR3 no se quede con la miniatura pensada para DPR2. Solo si es una
      // imagen del CDN propio: fuera de él no hay variantes de resolución que ofrecer
      // y srcset repetiría dos veces la misma URL sin motivo. Todo determinista
      // servidor↔cliente (nada de offsetWidth ni devicePixelRatio aquí).
      const srcsetBase = this.srcset() || src;
      if (srcsetBase && IMAGE_ORIGIN.test(srcsetBase)) {
        const width2 = this.eagerWidth2() || Math.round(this.eagerWidth() * 1.5);
        const url1 = this.srcset() ? this.formatEagerImage(srcsetBase, this.eagerWidth()) : url;
        const url2 = this.formatEagerImage(srcsetBase, width2);
        this.srcsetAttr = `${url1} ${this.eagerWidth()}w, ${url2} ${width2}w`;
        this.sizesAttr = this.eagerSizes() || `${Math.round(this.eagerWidth() / 2)}px`;
      } else {
        const srcset = this.srcset();
        this.srcsetAttr = srcset ? this.formatEagerImage(srcset, this.eagerWidth()) : url;
      }

      // Las tarjetas eager son las candidatas a LCP (Lighthouse 31/10: el LCP fue la 2.ª).
      this.fetchPriorityAttr = 'high';
      this.decodingAttr = 'async';
      this.loadingAttr = 'eager';
      return;
    }

    if (isPlatformServer(this.platformId)) {
      // SSR: URL real + loading="lazy" nativo. El navegador la carga sin JS.
      this.loadImageBase();
      return;
    }

    // Cliente: primero la MISMA URL determinista que emite el SSR (si el nodo viene
    // del servidor, se adopta tal cual; si el componente se re-renderiza en cliente
    // —ngSkipHydration, navegación CSR— la URL coincide con la que el navegador ya
    // tiene en caché, así que no hay parpadeo gris↔foto ni segunda descarga). Solo
    // queda subir la resolución si la caja es claramente mayor que el ancho base, y
    // solo cuando entra en pantalla.
    if (!this.adoptSsrImage()) this.loadImageBase();
    if (this.canLazyLoad()) this.lazyLoadImage(); else this.loadImage();
  }

  private loadResponsiveImage(): boolean {
    const sizes = this.responsiveSizes();
    if (!this.resize || !sizes || !this.responsiveWidths()) return false;
    const candidates = imageSrcset(this.srcset() || this.src(), this.responsiveWidths(), this.webp);
    if (!candidates) return false;
    this.srcAttr = this.webp ? webpImageUrl(this.src()) : this.src();
    this.srcAttr = imageWidthUrl(this.srcAttr, this.ssrWidth() || this.eagerWidth());
    this.srcsetAttr = candidates;
    this.sizesAttr = sizes;
    this.loadingAttr = this.eager() ? 'eager' : 'lazy';
    this.fetchPriorityAttr = this.eager() ? 'high' : null;
    this.decodingAttr = 'async';
    this.elementClass = '';
    this.adopted = true; // Retain the single original-image retry on failure.
    // Keep native DPR/viewport selection, without a manual resolution swap.
    // Anticipate within the actual scroll root using the same candidates;
    // native lazy loading alone can start too late inside Ionic content.
    if (!this.eager() && !isPlatformServer(this.platformId) && this.canLazyLoad()) {
      this.stopObserving?.();
      this.stopObserving = observeImageOnce(this.el.nativeElement, this.margenDeAnticipacion(), () => {
        this.stopObserving = null;
        if (this.destroyed) return;
        this.loadingAttr = 'eager';
        this.cdr.markForCheck();
      });
    }
    return true;
  }

  /** URL real a un ancho fijo + webp (determinista servidor↔cliente). */
  private formatEagerImage(image: string, width: number): string {
    if (!image) return image;
    if (!IMAGE_ORIGIN.test(image)) return image;
    image = this.setResolutionInUrl(image, width);
    return webpImageUrl(image);
  }

  /**
   * Ancho del hero por dispositivo, con el MISMO criterio que el servidor (User-Agent,
   * no ancho de ventana: el ancho no existe en SSR y cambiaría al hidratar). Réplica de
   * anchoHero() en social-meta.ts; si dejan de coincidir, doble descarga.
   */
  private anchoHeroPx(): number {
    const ua = this.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    return heroWidth(/mobile|android|iphone|ipod|iemobile|blackberry|opera mini|windows phone/i.test(ua));
  }

  /**
   * URL de la imagen hero para modo priority: ancho fijo por dispositivo + webp, igual
   * que heroImageUrl() en social-meta.ts, para que el <img> pida la MISMA URL que el
   * preload del servidor y no la descargue dos veces.
   */
  private formatPriorityImage(image: string): string {
    if (!image) return image;
    // Igual criterio que ngOnInit: fuera del CDN propio la imagen va tal cual.
    if (!IMAGE_ORIGIN.test(image)) return image;
    image = this.setResolutionInUrl(image, this.anchoHeroPx());
    return webpImageUrl(image);
  }

  /**
   * Estado base de una imagen lazy (servidor Y cliente): la URL real a ancho fijo
   * (ssrWidth, si no eagerWidth) con loading="lazy" nativo, para que se vea sin
   * esperar al bundle. Fuera del CDN propio la URL va tal cual (no hay variantes).
   * Sin src, el respaldo de siempre.
   */
  private loadImageBase() {
    const src = this.src();
    if (!src) {
      this.loadImageStatic();
      return;
    }
    const width = this.ssrWidth() || this.eagerWidth();
    const url = this.formatEagerImage(src, width);
    this.srcAttr = url;
    const srcset = this.srcset();
    this.srcsetAttr = srcset ? this.formatEagerImage(srcset, width) : url;
    this.loadingAttr = 'lazy';
    this.decodingAttr = 'async';
    this.elementClass = '';
    this.adoptedWidth = /\/r\/\d+\//.test(url) ? width : 0;
    this.adopted = true;
    this.cdr.markForCheck();
  }

  /**
   * Cliente al hidratar: si el <img> del SSR ya trae una URL real (no el placeholder
   * ni el respaldo) de ESTA misma imagen y no ha fallado, se adopta tal cual para no
   * volver a pintar el placeholder ni pedirla dos veces. Devuelve true si se adoptó.
   */
  private adoptSsrImage(): boolean {
    const el: HTMLImageElement = this.el.nativeElement;
    const existing = el.getAttribute && el.getAttribute('src');
    if (!existing || existing.startsWith('data:') || existing.startsWith(LazyloadDirective.FALLBACK_SRC)) return false;
    const src = this.src();
    if (src && !this.sameImage(existing, src)) return false;
    // Emitida por el servidor pero fallida (p. ej. 415 del resizer en frío): ruta
    // clásica (placeholder + observer + respaldo), que reintenta a otro ancho.
    if (el.complete && el.naturalWidth === 0) return false;

    this.srcAttr = existing;
    this.srcsetAttr = el.getAttribute('srcset') || existing;
    this.loadingAttr = el.getAttribute('loading') || 'lazy';
    this.decodingAttr = el.getAttribute('decoding') || 'async';
    this.elementClass = '';
    const m = /\/r\/(\d+)\//.exec(existing);
    this.adoptedWidth = m ? parseInt(m[1], 10) : 0;
    this.adopted = true;
    // Anticipación y resolución comparten lazyLoadImage; priority no crea observadores.
    return true;
  }

  /** Misma imagen ignorando el ancho /r/N/ y la extensión (webp vs jpg/png). */
  private sameImage(a: string, b: string): boolean {
    const norm = (u: string) => u.replace(/\/r\/\d+\//, '/').replace(/\.(png|jpe?g|webp)(\?|#|$)/i, '$2');
    return norm(a) === norm(b);
  }

  private canLazyLoad() {
    return window && 'IntersectionObserver' in window;
  }

  /**
   * Cuánto antes de entrar en pantalla se empieza a pedir la imagen. Eran 50 px en móvil y
   * 100 en escritorio, que con el dedo en marcha es nada: la foto empezaba a bajar cuando ya
   * la estabas mirando. 500/700 px es aproximadamente una pantalla de anticipación, así que
   * llega cargada. No afecta al arranque: estas peticiones salen después del primer pintado.
   * Reportado por el dueño el 17/09 («las imágenes tardan más en cargar que antes») al abrir
   * el SSR de los listados, que es cuando se notó.
   */
  private static margenCacheado: string | null = null;

  private margenDeAnticipacion(): string {
    // CACHEADO A PROPÓSITO, y es lo que lo hace barato: `window.innerWidth` fuerza al
    // navegador a recalcular estilo y layout si el DOM está sucio, y esto se llamaba UNA VEZ
    // POR IMAGEN al crear su observador. En un listado con poda —vistas montándose y
    // destruyéndose mientras te desplazas— eso son decenas de lecturas forzadas dentro del
    // scroll. Medido con el perfilador de V8 sobre 12 pantallas de /cupones/: era la función
    // propia más vista del perfil. Se calcula una vez por ancho de ventana y se invalida en
    // el resize (el mismo evento que ya refresca el breakpoint).
    if (LazyloadDirective.margenCacheado === null) {
      // ~2 screens ahead: a momentum fling (5,000-8,000 px/s) used to outrun 500 px (01/11/2026).
      LazyloadDirective.margenCacheado = (window && window.innerWidth < 768) ? '1500px' : '1200px';
    }
    return LazyloadDirective.margenCacheado;
  }

  /** Lo llama el listener de resize de la app: el ancho cambió, el margen puede cambiar. */
  static olvidarMargen() { LazyloadDirective.margenCacheado = null; }

  private lazyLoadImage() {
    this.stopObserving?.();
    this.stopObserving = observeImageOnce(this.el.nativeElement, this.margenDeAnticipacion(), () => {
      this.stopObserving = null;
      if (this.destroyed) return;
      // Un solo aviso adelanta la descarga nativa y ajusta la resolución si hace falta.
      this.loadingAttr = 'eager';
      this.loadImage();
      this.cdr.markForCheck();
    });
  }

  private loadImage() {
    if (this.adopted) {
      // Solo mejora de resolución: si la caja no es claramente mayor (>30 %) que el
      // ancho que emitió el servidor, quedarse con la foto ya descargada (evita un
      // segundo fetch casi idéntico en escritorio a DPR1: 166 → 187).
      const needed = this.neededWidth();
      if (!needed || !this.adoptedWidth || needed <= this.adoptedWidth * 1.3) return;
    }

    const srcset = this.srcset();
    if (srcset) {
      this.srcsetAttr = this.formatImage(srcset);
    } else {
      this.srcsetAttr = this.formatImage(this.src());
    }

    const src = this.src();
    if (src) {
      this.srcAttr = this.formatImage(src);
    }
    this.cdr.markForCheck();
  }

  setResolutionInUrl(image_url: string, res = 1) {
    return imageWidthUrl(image_url, res);
  }

  private loadImageStatic() {
    if (this.blurred && this.src()) {
      // IMG-3: placeholder inline, no la miniatura real /r/10 (0 peticiones).
      this.srcsetAttr = LazyloadDirective.PLACEHOLDER_SRC;
      this.srcAttr = LazyloadDirective.PLACEHOLDER_SRC;
    } else {
      this.srcsetAttr = LazyloadDirective.FALLBACK_SRC;
      this.srcAttr = LazyloadDirective.FALLBACK_SRC;
    }
    this.cdr.markForCheck();
  }

  // Segundo intento (original sin /r/N/) ya consumido en modo eager (IMG-9): evita
  // reintentar en bucle si el original también falla.
  private eagerRetried = false;

  imageError(e) {
    if (this.createMode()) return;

    const src = this.src();
    const eager = this.eager();
    if ((eager || this.adopted) && !this.eagerRetried && src) {
      // El resizer devuelve 415 de forma intermitente en frío (variante aún no
      // generada): antes de dejar la tarjeta rota, reintentar UNA vez con el
      // original sin /r/N/ (this.src, tal cual llega el @Input, sin formatear).
      // Vale también para el modo base (URL real del SSR).
      this.eagerRetried = true;
      this.elementClass = '';
      this.srcsetAttr = originalImageUrl(src);
      this.srcAttr = originalImageUrl(src);
      this.sizesAttr = null;
      this.adoptedWidth = 0; // ya no hay variante /r/N/ que mejorar
      this.cdr.markForCheck();
      return;
    }

    if (this.blurred || eager || this.adopted) {
      this.elementClass = '';
      this.srcsetAttr = LazyloadDirective.FALLBACK_SRC;
      this.srcAttr = LazyloadDirective.FALLBACK_SRC;
      this.sizesAttr = null;
      this.cdr.markForCheck();
    }
  }
}
